import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Award, TrendingUp, Loader2, Sparkles, Share2, Download } from "lucide-react";
import { usePageTitle } from "@/lib/seo";
import { useT } from "@/lib/i18n";

const TIER_STYLE = {
  Elite:    { bg: "from-violet-500 to-fuchsia-500", label: "Elite",    text: "text-violet-900" },
  Prime:    { bg: "from-emerald-500 to-teal-500",   label: "Prime",    text: "text-emerald-900" },
  Trusted:  { bg: "from-sky-500 to-blue-500",       label: "Trusted",  text: "text-sky-900" },
  Growing:  { bg: "from-amber-500 to-orange-500",   label: "Growing",  text: "text-amber-900" },
  Building: { bg: "from-slate-500 to-slate-700",    label: "Building", text: "text-slate-900" },
};

export default function TenantScore() {
  usePageTitle("Rent-on-Time Score — Central Rentals Canada");
  const t = useT();
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [s, h] = await Promise.all([
          api.get("/tenant/score"),
          api.get("/tenant/score/history"),
        ]);
        setData(s.data);
        setHistory(h.data.history || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Couldn't load your score.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const share = async () => {
    if (!data) return;
    const text = `I just hit a ${data.tier} rent-on-time score of ${data.score}/1000 on Central Rentals Canada!`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "My Rent-on-Time Score", text, url: "https://centralrentals.ca" });
      } else {
        await navigator.clipboard.writeText(`${text} ${window.location.href}`);
        toast.success("Copied to clipboard!");
      }
    } catch { /* user cancelled */ }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-emerald-600 animate-spin"/>
      </div>
    );
  }
  if (!data) return null;

  const tier = TIER_STYLE[data.tier] || TIER_STYLE.Building;
  const pctOfMax = Math.round((data.score / 1000) * 100);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-5 md:px-8 py-10" data-testid="tenant-score-page">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-sky-100 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-sky-700"/>
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-sky-700">{t("score.eyebrow", "Tenant smart tool")}</div>
            <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter text-slate-900 leading-tight mt-1">{t("score.title", "Rent-on-Time Score")}</h1>
            <p className="mt-2 text-slate-500 text-sm max-w-2xl">{t("score.subtitle", "Every on-time rent payment builds your rental credit. Share your badge when you apply to your next place.")}</p>
          </div>
        </div>

        {/* Score card */}
        <section className={`mt-8 rounded-3xl p-8 bg-gradient-to-br ${tier.bg} text-white shadow-lg overflow-hidden relative`} data-testid="score-card">
          <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10"/>
          <div className="absolute -right-16 -bottom-12 w-52 h-52 rounded-full bg-white/5"/>
          <div className="relative">
            <div className="text-xs uppercase tracking-[0.2em] font-bold opacity-80">{t("score.your_score", "Your score")}</div>
            <div className="mt-2 flex items-baseline gap-3">
              <div className="text-6xl md:text-7xl font-black tracking-tighter tabular-nums" data-testid="score-number">{data.score}</div>
              <div className="text-xl opacity-80">/ 1000</div>
            </div>
            <div className="mt-2 inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-3 py-1 text-sm font-semibold">
              <Sparkles className="w-4 h-4"/> {tier.label} tier
            </div>

            {/* Progress bar */}
            <div className="mt-6 w-full h-2 bg-white/20 rounded-full overflow-hidden">
              <div className="h-full bg-white rounded-full transition-all" style={{ width: `${pctOfMax}%` }}/>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="score-stats">
          <StatCard label={t("score.on_time_count", "On-time payments")} value={data.on_time_count} sub={`of ${data.total_payments} total`}/>
          <StatCard label={t("score.on_time_rate", "On-time rate")} value={`${data.on_time_pct}%`} sub="rolling average"/>
          <StatCard label={t("score.current_streak", "Current streak")} value={`${data.current_streak} mo`} sub="consecutive"/>
          <StatCard label={t("score.longest_streak", "Longest streak")} value={`${data.longest_streak} mo`} sub="all time"/>
        </section>

        {/* Badges */}
        {data.badges?.length > 0 && (
          <section className="mt-8" data-testid="score-badges">
            <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{t("score.badges_earned", "Badges earned")}</h2>
            <div className="mt-3 flex flex-wrap gap-3">
              {data.badges.map((b) => (
                <div key={b.key} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-slate-200 shadow-sm" data-testid={`badge-${b.key}`}>
                  <Award className="w-4 h-4 text-amber-500"/>
                  <div>
                    <div className="text-sm font-bold text-slate-900">{b.label}</div>
                    <div className="text-xs text-slate-500">{b.months} consecutive months on-time</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Score breakdown */}
        <section className="mt-8" data-testid="score-breakdown">
          <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{t("score.how_calculated", "How your score is calculated")}</h2>
          {data.has_history === false || (data.total_payments || 0) === 0 ? (
            // No real payment data yet → the "0 recorded" table was confusing
            // tenants into thinking their score had already been dinged. Show
            // a clean prompt instead and reveal the full breakdown only once
            // there's actual history to grade.
            <div className="mt-3 rounded-2xl border-2 border-dashed border-slate-200 bg-white p-8 text-center" data-testid="score-breakdown-empty">
              <p className="text-sm text-slate-500 leading-relaxed max-w-md mx-auto">
                Your rent-on-time score will unlock once your landlord records your first
                payment. Every on-time or early payment adds points; late payments cost
                points. Nothing to grade yet.
              </p>
            </div>
          ) : (
            <div className="mt-3 bg-white rounded-2xl border border-slate-200 divide-y divide-slate-100">
              <BreakdownRow label="Starting score" value="500 pts" note="Everyone starts here."/>
              <BreakdownRow label="Every early payment (3+ days early)" value="+12 pts" note={`${data.breakdown.early} recorded`}/>
              <BreakdownRow label="Every on-time payment" value="+10 pts" note={`${data.breakdown.on_time} recorded`}/>
              <BreakdownRow label="Late 1–3 days" value="-5 pts" note={`${data.breakdown.late_1_3} recorded`}/>
              <BreakdownRow label="Late 4–14 days" value="-20 pts" note={`${data.breakdown.late_4_14} recorded`}/>
              <BreakdownRow label="Late 15+ days" value="-50 pts" note={`${data.breakdown.late_15_plus} recorded`}/>
              <BreakdownRow label="Unpaid (still outstanding)" value="-75 pts" note={`${data.breakdown.unpaid} recorded`}/>
              <BreakdownRow label="Consecutive-month streak bonus" value="+5 pts every 3 months" note={`${data.longest_streak} peak streak`}/>
            </div>
          )}
        </section>

        {/* Actions */}
        <section className="mt-8 flex flex-wrap gap-3" data-testid="score-actions">
          <button
            onClick={share}
            data-testid="score-share-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold"
          >
            <Share2 className="w-4 h-4"/> {t("score.share_btn", "Share my score")}
          </button>
          <a
            href="/wellness"
            data-testid="score-receipt-link"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white border border-slate-200 hover:border-slate-300 text-slate-700 text-sm font-semibold"
          >
            <Download className="w-4 h-4"/> {t("score.download_history", "Download rent history")}
          </a>
        </section>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4">
      <div className="text-xs text-slate-500 uppercase tracking-wider">{label}</div>
      <div className="mt-1 text-2xl font-bold tracking-tight text-slate-900 tabular-nums">{value}</div>
      <div className="text-xs text-slate-400 mt-0.5">{sub}</div>
    </div>
  );
}

function BreakdownRow({ label, value, note }) {
  return (
    <div className="p-4 flex items-center justify-between gap-4">
      <div>
        <div className="text-sm font-medium text-slate-800">{label}</div>
        <div className="text-xs text-slate-500">{note}</div>
      </div>
      <div className="text-sm font-bold text-slate-900 tabular-nums">{value}</div>
    </div>
  );
}
